import { $, $$ } from '../common/dom.ts';
import { DEFAULT_VOICE } from '../../shared/voices.ts';
import { AUDIO_CHANNELS, type AudioChannel, type SoundBoard } from './sound.ts';

/** Phrase d'essai de la voix des dialogues, et écart entre ses syllabes (ms). */
const VOICE_SAMPLE = 'Bonjour, belle journée !';
const VOICE_GAP_MS = 75;
/** Écart entre les pas d'essai (ms). */
const STEP_GAP_MS = 260;

/**
 * Réglages du son, dans la fenêtre Options : volume général (ou son coupé) et volume de chaque type de son (musique,
 * ambiance, effets spéciaux, voix des dialogues, bruits de pas), chacun avec un essai, retenus dans ce navigateur.
 */
export class SoundSettings {
  private readonly root: HTMLElement;
  private readonly volume: HTMLInputElement;
  private readonly mute: HTMLInputElement;
  private readonly channels: HTMLInputElement[];
  private readonly sounds: SoundBoard;

  /** `root` : la fenêtre qui contient les réglages (voir OptionsPanel). */
  constructor(root: HTMLElement, sounds: SoundBoard) {
    this.root = root;
    this.volume = $<HTMLInputElement>('#option-volume', root);
    this.mute = $<HTMLInputElement>('#option-mute', root);
    this.channels = $$<HTMLInputElement>('[data-channel]', root);
    this.sounds = sounds;
    this.volume.addEventListener('input', () => {
      sounds.setVolume(Number(this.volume.value) / 100);
      this.render();
    });
    // Un petit son au lâcher du curseur, pour entendre le nouveau volume.
    this.volume.addEventListener('change', () => sounds.play('select'));
    this.mute.addEventListener('change', () => {
      sounds.setMuted(this.mute.checked);
      if (!this.mute.checked) sounds.play('select');
      this.render();
    });
    for (const slider of this.channels) {
      const channel = slider.dataset.channel as AudioChannel;
      slider.addEventListener('input', () => {
        sounds.setChannelVolume(channel, Number(slider.value) / 100);
        this.render();
      });
      // Musique et ambiance s'entendent déjà : les autres jouent leur essai au lâcher du curseur.
      slider.addEventListener('change', () => this.test(channel));
    }
    for (const button of $$<HTMLButtonElement>('[data-test]', this.root)) {
      button.addEventListener('click', () => this.test(button.dataset.test as AudioChannel));
    }
  }

  /** Essai d'un type de son, au volume choisi. */
  private test(channel: AudioChannel): void {
    const { sounds } = this;
    switch (channel) {
      case 'effects': return sounds.play('capture');
      case 'voice': return [...VOICE_SAMPLE].filter((_, i) => i % 2 === 0).forEach((char, i) => setTimeout(() => sounds.speak(DEFAULT_VOICE, char), i * VOICE_GAP_MS));
      case 'footsteps': return (['grass', 'grass', 'stone', 'wood'] as const).forEach((kind, i) => setTimeout(() => sounds.footstep(kind), i * STEP_GAP_MS));
      case 'music':
      case 'ambience': return;
    }
  }

  render(): void {
    const { sounds } = this;
    this.volume.value = String(Math.round(sounds.volume * 100));
    this.volume.disabled = sounds.isMuted;
    $('#option-volume-value', this.root).textContent = sounds.isMuted ? 'Coupé' : `${Math.round(sounds.volume * 100)} %`;
    this.mute.checked = sounds.isMuted;
    for (const slider of this.channels) {
      const channel = slider.dataset.channel as AudioChannel;
      const volume = Math.round(sounds.channelVolume(channel) * 100);
      slider.value = String(volume);
      slider.disabled = sounds.isMuted;
      $(`[data-channel-value="${channel}"]`, this.root).textContent = sounds.isMuted ? '' : `${volume} %`;
      slider.setAttribute('aria-label', `Volume : ${AUDIO_CHANNELS[channel]}`);
    }
    for (const button of $$<HTMLButtonElement>('[data-test]', this.root)) button.disabled = sounds.isMuted;
  }
}
